// Import model
import { FieldOptions } from '../models/FieldOptions';

// Import base class serializer

// Import dependency types (models and enums used in properties)

// Import dependency serializers (for complex types)

// Import derived type serializers (for polymorphic deserialization)
import { SelectFieldOptionsSerializer } from './SelectFieldOptionsSerializer';
import { AddressFieldOptionsSerializer } from './AddressFieldOptionsSerializer';
import { AppointmentFieldOptionsSerializer } from './AppointmentFieldOptionsSerializer';
import { ArrayFieldOptionsSerializer } from './ArrayFieldOptionsSerializer';
import { BitwiseFlagFieldOptionsSerializer } from './BitwiseFlagFieldOptionsSerializer';
import { ChatReferenceFieldOptionsSerializer } from './ChatReferenceFieldOptionsSerializer';
import { CheckboxFieldOptionsSerializer } from './CheckboxFieldOptionsSerializer';
import { ChildrenFieldOptionsSerializer } from './ChildrenFieldOptionsSerializer';
import { DateRangeFieldOptionsSerializer } from './DateRangeFieldOptionsSerializer';
import { DictionaryFieldOptionsSerializer } from './DictionaryFieldOptionsSerializer';
import { EmailFieldOptionsSerializer } from './EmailFieldOptionsSerializer';
import { ExpressionFieldOptionsSerializer } from './ExpressionFieldOptionsSerializer';
import { FileFieldOptionsSerializer } from './FileFieldOptionsSerializer';
import { GenericFieldOptionsSerializer } from './GenericFieldOptionsSerializer';
import { HiddenFieldOptionsSerializer } from './HiddenFieldOptionsSerializer';
import { ImageFieldOptionsSerializer } from './ImageFieldOptionsSerializer';
import { LabelFieldOptionsSerializer } from './LabelFieldOptionsSerializer';
import { LocationDistanceFieldOptionsSerializer } from './LocationDistanceFieldOptionsSerializer';
import { LocationFieldOptionsSerializer } from './LocationFieldOptionsSerializer';
import { LookupFieldOptionsSerializer } from './LookupFieldOptionsSerializer';
import { NumberFieldOptionsSerializer } from './NumberFieldOptionsSerializer';
import { ObjectFieldOptionsSerializer } from './ObjectFieldOptionsSerializer';
import { PasswordFieldOptionsSerializer } from './PasswordFieldOptionsSerializer';
import { PhoneFieldOptionsSerializer } from './PhoneFieldOptionsSerializer';
import { PostalCodeFieldOptionsSerializer } from './PostalCodeFieldOptionsSerializer';
import { RelatedObjectsFieldOptionsSerializer } from './RelatedObjectsFieldOptionsSerializer';
import { TagsFieldOptionsSerializer } from './TagsFieldOptionsSerializer';
import { TextFieldOptionsSerializer } from './TextFieldOptionsSerializer';
import { TimeFieldOptionsSerializer } from './TimeFieldOptionsSerializer';
import { UrlFieldOptionsSerializer } from './UrlFieldOptionsSerializer';
import { MultiReferenceFieldOptionsSerializer } from './MultiReferenceFieldOptionsSerializer';
import { MultiSelectFieldOptionsSerializer } from './MultiSelectFieldOptionsSerializer';
import { ReferenceFieldOptionsSerializer } from './ReferenceFieldOptionsSerializer';
import { RemoteFileFieldOptionsSerializer } from './RemoteFileFieldOptionsSerializer';

/**
 * Serializer for FieldOptions - handles JSON serialization and deserialization with type validation
 */
export class FieldOptionsSerializer {
  /**
   * Deserializes a JSON string into a FieldOptions instance
   */
  static deserialize(json: string): FieldOptions {
    const obj = JSON.parse(json);
    return this.fromObject(obj);
  }

  /**
   * Deserializes a plain object into a FieldOptions instance
   */
  static fromObject(obj: any): FieldOptions {
    if (!obj) {
      throw new Error('Cannot deserialize null or undefined to FieldOptions');
    }

    // Handle polymorphic deserialization using discriminator
    const discriminator = obj['_t'];

    switch (discriminator) {
      case 'AddressFieldOptions':
        return AddressFieldOptionsSerializer.fromObject(obj);
      case 'AppointmentFieldOptions':
        return AppointmentFieldOptionsSerializer.fromObject(obj);
      case 'ArrayFieldOptions':
        return ArrayFieldOptionsSerializer.fromObject(obj);
      case 'BitwiseFlagFieldOptions':
        return BitwiseFlagFieldOptionsSerializer.fromObject(obj);
      case 'ChatReferenceFieldOptions':
        return ChatReferenceFieldOptionsSerializer.fromObject(obj);
      case 'CheckboxFieldOptions':
        return CheckboxFieldOptionsSerializer.fromObject(obj);
      case 'ChildrenFieldOptions':
        return ChildrenFieldOptionsSerializer.fromObject(obj);
      case 'DateRangeFieldOptions':
        return DateRangeFieldOptionsSerializer.fromObject(obj);
      case 'DictionaryFieldOptions':
        return DictionaryFieldOptionsSerializer.fromObject(obj);
      case 'ExpressionFieldOptions':
        return ExpressionFieldOptionsSerializer.fromObject(obj);
      case 'EmailFieldOptions':
        return EmailFieldOptionsSerializer.fromObject(obj);
      case 'FileFieldOptions':
        return FileFieldOptionsSerializer.fromObject(obj);
      case 'GenericFieldOptions':
        return GenericFieldOptionsSerializer.fromObject(obj);
      case 'HiddenFieldOptions':
        return HiddenFieldOptionsSerializer.fromObject(obj);
      case 'ImageFieldOptions':
        return ImageFieldOptionsSerializer.fromObject(obj);
      case 'LabelFieldOptions':
        return LabelFieldOptionsSerializer.fromObject(obj);
      case 'LocationDistanceFieldOptions':
        return LocationDistanceFieldOptionsSerializer.fromObject(obj);
      case 'LocationFieldOptions':
        return LocationFieldOptionsSerializer.fromObject(obj);
      case 'LookupFieldOptions':
        return LookupFieldOptionsSerializer.fromObject(obj);
      case 'MultiReferenceFieldOptions':
        return MultiReferenceFieldOptionsSerializer.fromObject(obj);
      case 'MultiSelectFieldOptions':
        return MultiSelectFieldOptionsSerializer.fromObject(obj);
      case 'NumberFieldOptions':
        return NumberFieldOptionsSerializer.fromObject(obj);
      case 'ObjectFieldOptions':
        return ObjectFieldOptionsSerializer.fromObject(obj);
      case 'PasswordFieldOptions':
        return PasswordFieldOptionsSerializer.fromObject(obj);
      case 'PhoneFieldOptions':
        return PhoneFieldOptionsSerializer.fromObject(obj);
      case 'PostalCodeFieldOptions':
        return PostalCodeFieldOptionsSerializer.fromObject(obj);
      case 'ReferenceFieldOptions':
        return ReferenceFieldOptionsSerializer.fromObject(obj);
      case 'RelatedObjectsFieldOptions':
        return RelatedObjectsFieldOptionsSerializer.fromObject(obj);
      case 'RemoteFileFieldOptions':
        return RemoteFileFieldOptionsSerializer.fromObject(obj);
      case 'SelectFieldOptions':
        return SelectFieldOptionsSerializer.fromObject(obj);
      case 'TagsFieldOptions':
        return TagsFieldOptionsSerializer.fromObject(obj);
      case 'TextFieldOptions':
        return TextFieldOptionsSerializer.fromObject(obj);
      case 'TimeFieldOptions':
        return TimeFieldOptionsSerializer.fromObject(obj);
      case 'URLFieldOptions':
        return UrlFieldOptionsSerializer.fromObject(obj);
      default:
        // Fall through to base type deserialization
        break;
    }

    const instance: FieldOptions = {
      t: obj['_t'] as string,
      linkUrl: obj['LinkUrl'] as string | undefined,
      linkLabel: obj['LinkLabel'] as string | undefined,
    };

    return instance;
  }

  /**
   * Deserializes a JSON string into an array of FieldOptions instances
   */
  static deserializeArray(json: string): FieldOptions[] {
    const arr = JSON.parse(json);
    if (!Array.isArray(arr)) {
      throw new Error('Expected JSON array');
    }
    return arr.map(obj => this.fromObject(obj));
  }

  /**
   * Serializes a FieldOptions instance to a JSON string
   */
  static serialize(instance: FieldOptions): string {
    return JSON.stringify(this.toObject(instance));
  }

  /**
   * Converts a FieldOptions instance to a plain object
   */
  static toObject(instance: FieldOptions): any {
    return {
      '_t': instance.t,
      'LinkUrl': instance.linkUrl,
      'LinkLabel': instance.linkLabel,
    };
  }

  /**
   * Serializes an array of FieldOptions instances to a JSON string
   */
  static serializeArray(instances: FieldOptions[]): string {
    return JSON.stringify(instances.map(inst => this.toObject(inst)));
  }
}
